import { useState, useEffect } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { toast } from "sonner";
import { Search, FileSpreadsheet, Loader2 } from "lucide-react";

export default function ProductInquiryPage() {
  const [schemas, setSchemas] = useState([]);
  const [selectedSchema, setSelectedSchema] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [warehouses, setWarehouses] = useState([]);
  const [selectedWarehouse, setSelectedWarehouse] = useState("");
  const [results, setResults] = useState(null);
  const [columns, setColumns] = useState([]);
  const [loading, setLoading] = useState(false);
  const [whsLoading, setWhsLoading] = useState(false);

  useEffect(() => {
    const loadSchemas = async () => {
      try {
        const { data } = await api.get("/schemas");
        setSchemas(data);
        if (data.length > 0) setSelectedSchema(data[0]);
      } catch {
        toast.error("Failed to load schemas");
      }
    };
    loadSchemas();
  }, []);

  useEffect(() => {
    if (!selectedSchema) return;
    const loadWarehouses = async () => {
      setWhsLoading(true);
      try {
        const { data } = await api.get(`/inquiry/warehouses/${selectedSchema}`);
        setWarehouses(data);
      } catch {
        setWarehouses([]);
      } finally {
        setWhsLoading(false);
      }
    };
    loadWarehouses();
  }, [selectedSchema]);

  const handleSearch = async () => {
    if (!searchTerm.trim()) {
      toast.error("Enter a search term");
      return;
    }
    if (!selectedSchema) {
      toast.error("Select a schema");
      return;
    }
    setLoading(true);
    try {
      const { data } = await api.post("/inquiry/search", {
        schema: selectedSchema,
        search_term: searchTerm,
        warehouse: selectedWarehouse && selectedWarehouse !== "all" ? selectedWarehouse : null,
      });
      setColumns(data.columns);
      setResults(data.data);
      toast.success(`${data.row_count} results found`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Search failed");
    } finally {
      setLoading(false);
    }
  };

  const handleExport = async () => {
    if (!results || results.length === 0) return;
    try {
      const response = await api.post(
        "/inquiry/export",
        {
          schema: selectedSchema,
          search_term: searchTerm,
          warehouse: selectedWarehouse && selectedWarehouse !== "all" ? selectedWarehouse : null,
        },
        { responseType: "blob" }
      );
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", "product_inquiry.xlsx");
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Exported to Excel");
    } catch {
      toast.error("Export failed");
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter") handleSearch();
  };

  return (
    <div data-testid="product-inquiry-page">
      <h1
        className="text-2xl font-bold tracking-tight text-white mb-2"
        style={{ fontFamily: "Chivo, sans-serif" }}
      >
        Product Inquiry
      </h1>
      <p className="text-sm text-[#A3A3A3] mb-6">
        Search products by item name across warehouses
      </p>

      {/* Search Bar */}
      <div
        className="bg-[#141414] border border-[#262626] rounded-md p-4 mb-6"
        data-testid="inquiry-filter-bar"
      >
        <div className="flex flex-wrap items-end gap-4">
          <div>
            <label className="text-xs font-semibold tracking-wider uppercase text-[#A3A3A3] mb-1.5 block">
              Schema
            </label>
            <Select value={selectedSchema} onValueChange={setSelectedSchema}>
              <SelectTrigger
                className="w-[200px] bg-[#0A0A0A] border-[#262626]"
                data-testid="inquiry-schema-select"
              >
                <SelectValue placeholder="Select schema" />
              </SelectTrigger>
              <SelectContent>
                {schemas.map((s) => (
                  <SelectItem key={s} value={s}>
                    {s}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="flex-1 min-w-[200px]">
            <label className="text-xs font-semibold tracking-wider uppercase text-[#A3A3A3] mb-1.5 block">
              Item Name <span className="text-red-400">*</span>
            </label>
            <Input
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Search by item name..."
              data-testid="inquiry-search-input"
              className="bg-[#0A0A0A] border-[#262626]"
            />
          </div>

          <div>
            <label className="text-xs font-semibold tracking-wider uppercase text-[#A3A3A3] mb-1.5 block">
              Warehouse
            </label>
            <Select value={selectedWarehouse} onValueChange={setSelectedWarehouse}>
              <SelectTrigger
                className="w-[180px] bg-[#0A0A0A] border-[#262626]"
                data-testid="inquiry-warehouse-select"
              >
                <SelectValue placeholder={whsLoading ? "Loading..." : "All"} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Warehouses</SelectItem>
                {warehouses.map((w) => (
                  <SelectItem key={w} value={w}>
                    {w}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <Button
            onClick={handleSearch}
            disabled={loading}
            data-testid="inquiry-search-button"
            className="bg-[#0066FF] hover:bg-[#005CE6] text-white"
          >
            {loading ? (
              <Loader2 size={16} className="mr-2 animate-spin" />
            ) : (
              <Search size={16} className="mr-2" />
            )}
            Search
          </Button>
        </div>
      </div>

      {/* Results */}
      {results && (
        <div
          className="bg-[#141414] border border-[#262626] rounded-md"
          data-testid="inquiry-results"
        >
          <div className="p-3 border-b border-[#262626] flex items-center justify-between">
            <span className="text-sm text-[#A3A3A3]" data-testid="inquiry-row-count">
              {results.length} results
            </span>
            <Button
              variant="outline"
              size="sm"
              onClick={handleExport}
              data-testid="inquiry-export-button"
              className="border-[#262626] text-white hover:bg-[#1E1E1E]"
            >
              <FileSpreadsheet size={16} className="mr-2" /> Export
            </Button>
          </div>
          <div className="table-scroll-wrapper">
            <Table>
              <TableHeader>
                <TableRow className="border-[#262626] hover:bg-transparent">
                  {columns.map((col) => (
                    <TableHead
                      key={col}
                      className="text-xs font-semibold tracking-wider uppercase text-[#A3A3A3] whitespace-nowrap sticky top-0 bg-[#141414]"
                    >
                      {col}
                    </TableHead>
                  ))}
                </TableRow>
              </TableHeader>
              <TableBody>
                {results.length === 0 ? (
                  <TableRow>
                    <TableCell
                      colSpan={columns.length}
                      className="text-center text-[#A3A3A3] py-8"
                    >
                      No results found
                    </TableCell>
                  </TableRow>
                ) : (
                  results.map((row, i) => (
                    <TableRow key={i} className="border-[#262626] hover:bg-[#1E1E1E]">
                      {columns.map((col) => (
                        <TableCell
                          key={col}
                          className="text-sm whitespace-nowrap text-white"
                        >
                          {row[col] != null ? String(row[col]) : ""}
                        </TableCell>
                      ))}
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </div>
      )}
    </div>
  );
}
